import ExcelJS from "exceljs";
import * as FileSaver from "file-saver";
import { storeResultantBom } from "../../../actions";
import {
  buildAssemblySpecs,
  formatMoneyAmount,
  FREIGHT_TARIFF_FOOTER_ARGB,
  getExportManufacturer,
  getExportManufacturerPartNumber,
  getExportManufacturerPartNumberCellValue,
  getTableColumnKeys,
  hasLineAlternateSelected,
  isBomSectionRow,
  isBomTotalRow,
  isExportManufacturerColumn,
  isExportMpnColumn,
  isLeadlessLine,
  isObsoleteLine,
  isUnmatchedLine,
  LEADLESS_HIGHLIGHT_ARGB,
  OBSOLETE_HIGHLIGHT_ARGB,
  prepareBomlinesForExport,
  UNMATCHED_HIGHLIGHT_ARGB,
} from "../../../constants/digiKeyColumns";
import { normalizeIssueReport } from "../../../constants/issueReportUtils";
import { isRealBomLine } from "../../../constants/quoteQuantities";
import { addIssueReportWorksheet } from "../../../utils/issueReportExport";

const addATemplateRowWithBasicCellFormat = (sheet, titleData) => {
  let columnsCount = Object.keys(titleData).length;
  let rowCellsStrings = Array(columnsCount).join(".").split(".");
  let rowTemplate = sheet.addRow(rowCellsStrings);
  rowTemplate._cells.forEach((cell, index) => {
    cell.border = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" },
    };
  });
  return rowTemplate;
};

const addSheetTitleRowAndGetInputColumnsCount = (
  rowTemplate,
  titleData,
  columnNames,
) => {
  let inputColumnsCount = 0;
  let titleRow = Object.keys(titleData);
  rowTemplate._cells.forEach((cell, index) => {
    let argb = "6c757d";
    let cellValue = columnNames.find(
      (item) => item.code_name === titleRow[index],
    );

    if (!cellValue) {
      cellValue = { name: titleRow[index] };
      argb = titleRow[index].indexOf("Quote ") !== -1 ? "0080ff" : "04b78a";
    } else {
      inputColumnsCount++;
    }

    cell.value = cellValue.name;
    cell.font = {
      color: { argb: "FFFFFFFF" },
      bold: true,
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: argb },
    };
  });

  return inputColumnsCount;
};

const isQuantityCellWithInvalidValue = (
  cellColumnTitle,
  invalidQuantityColumns,
  index,
) => {
  return (
    cellColumnTitle === "quantity" && invalidQuantityColumns.includes(index + 1)
  );
};

const getRichDescriptionCellValue = (descriptionObject) => {
  let descriptionCellRichTextArray = [];

  // eslint-disable-next-line array-callback-return
  descriptionObject.description.map(function (t) {
    if (t.key) {
      descriptionCellRichTextArray.push({
        font: {
          color: { argb: "636e95" },
        },
        text: t.key + ": ",
      });

      let value = t.link ? t.link : t.value ? t.value : "n/a";
      descriptionCellRichTextArray.push({
        text: value + " \r\n",
      });
    }
  });
  return {
    richText: descriptionCellRichTextArray,
  };
};

const parseExcelMoneyValue = (cellValue) => {
  if (cellValue == null || cellValue === "") {
    return 0;
  }
  if (typeof cellValue === "number") {
    return Number.isFinite(cellValue) ? cellValue : 0;
  }
  let text = "";
  if (typeof cellValue === "object" && Array.isArray(cellValue.richText)) {
    text = cellValue.richText.map((part) => part.text || "").join("");
  } else {
    text = String(cellValue);
  }
  const normalized = text.replace(/,/g, "").trim();
  if (!normalized || /^n\/a$/i.test(normalized)) {
    return 0;
  }
  const amount = parseFloat(normalized.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const excelColumnLetter = (columnNumber) => {
  let n = Number(columnNumber);
  let letters = "";
  while (n > 0) {
    const remainder = (n - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
};

const formatExportNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return "0";
  }
  return Number.isInteger(n) ? String(n) : String(n);
};

const thinBorder = {
  top: { style: "thin" },
  bottom: { style: "thin" },
  left: { style: "thin" },
  right: { style: "thin" },
};

const getRichResultCellValue = (resultObject) => {
  let resultCellRichTextArray = [];

  if (resultObject.title) {
    resultCellRichTextArray.push({
      font: {
        color: { argb: "4b499f" },
      },
      text: resultObject.title + " \r\n",
    });
  }

  if (resultObject?.text?.length > 0) {
    // eslint-disable-next-line array-callback-return
    resultObject.text.map(function (t) {
      if (t.text) {
        resultCellRichTextArray.push({
          font: {
            italic: t.class === "tdItalicSubTitle",
          },
          text: t.text,
        });
      }
    });
  }
  return {
    richText: resultCellRichTextArray,
  };
};

const getColorForResultCell = (resultObject) => {
  switch (resultObject.class) {
    case "bg-green":
      return "effbf8";
    case "bg-yellow":
      return "ffedad";
    case "bg-light-maroon":
      return "fdeeee";
    case "bg-gray-opacity":
      return "e9e9fc";
    default:
      return "ffffff";
  }
};

const addSheetDataRow = (
  rowTemplate,
  titleData,
  item,
  invalidQuantityColumns,
  rowIndex,
  sheet,
  lastBomLineIndex,
  bomChecklists,
) => {
  if (isBomSectionRow(item)) {
    const title =
      item._section_title ||
      Object.values(item).find(
        (value) => typeof value === "string" && value.trim() !== "",
      ) ||
      "";
    rowTemplate._cells.forEach((cell, index) => {
      cell.value = index === 0 ? title : "";
      cell.font = {
        color: { argb: "FFFFFFFF" },
        bold: true,
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "6c757d" },
      };
    });
    return;
  }

  let titleRow = Object.keys(titleData);
  const leadlessRow = isLeadlessLine(item);
  const obsoleteRow = isObsoleteLine(item);
  const unmatchedRow = isUnmatchedLine(item);
  rowTemplate._cells.forEach((cell, index) => {
    const columnKey = titleRow[index];
    let cellValue = item[columnKey];

    if (isExportMpnColumn(columnKey)) {
      const richMpn = getExportManufacturerPartNumberCellValue(item);
      cellValue = richMpn || getExportManufacturerPartNumber(item);
    } else if (isExportManufacturerColumn(columnKey)) {
      cellValue = getExportManufacturer(item);
    }

    let argb = isQuantityCellWithInvalidValue(
      columnKey,
      invalidQuantityColumns,
      rowIndex,
    )
      ? "fdeeee"
      : obsoleteRow
        ? OBSOLETE_HIGHLIGHT_ARGB
        : unmatchedRow
          ? UNMATCHED_HIGHLIGHT_ARGB
          : leadlessRow
            ? LEADLESS_HIGHLIGHT_ARGB
            : "ffffff";

    if (
      cellValue != null &&
      typeof cellValue === "object" &&
      cellValue.description != null
    ) {
      cellValue = getRichDescriptionCellValue(cellValue);
    } else if (
      cellValue != null &&
      typeof cellValue === "object" &&
      cellValue.richText
    ) {
      // Already formatted (e.g. obsolete alternate MPN cell).
    } else if (cellValue != null && typeof cellValue === "object") {
      argb = getColorForResultCell(cellValue);
      // Keep leadless/obsolete tint when quote/result cell has no status fill
      if (argb === "ffffff" && obsoleteRow) {
        argb = OBSOLETE_HIGHLIGHT_ARGB;
      } else if (argb === "ffffff" && unmatchedRow) {
        argb = UNMATCHED_HIGHLIGHT_ARGB;
      } else if (argb === "ffffff" && leadlessRow) {
        argb = LEADLESS_HIGHLIGHT_ARGB;
      }
      cellValue = getRichResultCellValue(cellValue);
    }
    const regexTest = /^BOM Manager Result \d+$/;
    if (
      cellValue &&
      regexTest.test(columnKey) &&
      cellValue?.richText?.length === 1
    ) {
      if (cellValue?.richText[0].text === "Multiple Manufacturers") {
        cellValue = {
          richText: [
            {
              text: cellValue?.richText[0].text,
              font: {
                color: {
                  argb: "ff0000",
                  theme: 1,
                },
                bold: true,
              },
            },
          ],
        };
      }
    }
    if (
      cellValue &&
      typeof cellValue !== "object" &&
      (isExportMpnColumn(columnKey) || columnKey === "designator") &&
      item["designator_color"] &&
      !hasLineAlternateSelected(item)
    ) {
      cellValue = {
        richText: [
          {
            text: cellValue,
            font: {
              color: {
                argb: "a02b93",
                theme: 1,
              },
              bold: true,
            },
          },
        ],
      };
    }
    cell.value = cellValue;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: argb,
      },
    };
    cell.alignment = { wrapText: false };

    const regex = /^Quote \d+$/;
    if (regex.test(columnKey) && (isRealBomLine(item) || isBomTotalRow(item))) {
      const quoteAmount = parseExcelMoneyValue(cellValue);
      const quoteCell = sheet.getCell(cell?._address);
      quoteCell.numFmt = "$###0.00";
      quoteCell.value = quoteAmount;
    }

    if (
      /^Tariff Charge \d+$/.test(columnKey) &&
      (isRealBomLine(item) || isBomTotalRow(item))
    ) {
      const tariffCell = sheet.getCell(cell?._address);
      tariffCell.numFmt = "$###0.00";
      tariffCell.value = parseExcelMoneyValue(cellValue);
    }

    if (columnKey === "Quote 1" && rowIndex === lastBomLineIndex) {
      let listIndexNumber = 0;
      bomChecklists.forEach((checklist, listIndex) => {
        if (checklist?.active === 1) {
          let number = lastBomLineIndex + 4 + listIndexNumber;
          const cell2 = sheet.getCell("B" + number);
          cell2.value = checklist?.name;

          const cell1 = sheet.getCell("A" + number);
          cell1.dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: ['"SELECT,TRUE,FALSE"'],
            showErrorMessage: true,
            errorTitle: "Invalid selection",
            error: "Please select a value from the dropdown list.",
          };
          cell1.value = "SELECT";

          listIndexNumber++;
        }
      });
    }
  });
};

const styleBorderedCell = (
  cell,
  { bold = false, numFmt = null, align = "left" } = {},
) => {
  cell.border = thinBorder;
  cell.font = { bold };
  cell.alignment = { horizontal: align, vertical: "middle" };
  if (numFmt) {
    cell.numFmt = numFmt;
  }
};

const getLastQuoteSumExcelRow = (bomlines) => {
  let lastIndex = (bomlines || []).length - 1;
  while (
    lastIndex >= 0 &&
    (isBomTotalRow(bomlines[lastIndex]) || isBomSectionRow(bomlines[lastIndex]))
  ) {
    lastIndex -= 1;
  }
  if (lastIndex < 0) {
    return 2;
  }
  return lastIndex + 2;
};

const addLegacyFreightTariffFooterRows = (
  sheet,
  columnCount,
  { freightTotal = 0, tariffTotal = 0, currency = "$" } = {},
) => {
  const colCount = Math.max(1, columnCount || 1);

  const addFooterRow = (label) => {
    const values = Array(colCount).fill("");
    values[0] = label;
    const row = sheet.addRow(values);
    row._cells.forEach((cell, index) => {
      cell.border = thinBorder;
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: FREIGHT_TARIFF_FOOTER_ARGB },
      };
      if (index === 0) {
        cell.font = { bold: true };
      } else {
        cell.value = "";
      }
    });
    if (colCount > 1) {
      sheet.mergeCells(row.number, 1, row.number, colCount);
    }
  };

  addFooterRow(
    `Freight: ${currency}${Number.isFinite(Number(freightTotal)) ? Number(freightTotal) : 0}`,
  );

  const tariffValue = Number(tariffTotal);
  if (Number.isFinite(tariffValue) && tariffValue > 0) {
    addFooterRow(`Tariff Charge : ${formatMoneyAmount(tariffValue, currency)}`);
  }
};

const addAssemblySpecsTable = (
  sheet,
  bomlines,
  columnKeys,
  startRow,
  { mode = "lines", title = "Assembly Specs" } = {},
) => {
  const specs = buildAssemblySpecs(bomlines, mode);
  const firstQuoteCol =
    columnKeys.findIndex(
      (key) =>
        /^QuoteQty \d+$/.test(key) ||
        /^BOM Manager Result \d+$/.test(key) ||
        /^Quote \d+$/.test(key),
    ) + 1;
  let labelCol = 6;
  if (firstQuoteCol > 0 && labelCol + 1 >= firstQuoteCol) {
    labelCol = 3;
  }
  if (firstQuoteCol > 0 && labelCol + 1 >= firstQuoteCol) {
    labelCol = 1;
  }
  const valueCol = labelCol + 1;

  const headerLabel = sheet.getCell(startRow, labelCol);
  const headerValue = sheet.getCell(startRow, valueCol);
  headerLabel.value = title;
  headerLabel.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "d9d9d9" },
  };
  headerValue.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "d9d9d9" },
  };
  styleBorderedCell(headerLabel, { bold: true, align: "center" });
  styleBorderedCell(headerValue, { bold: true, align: "center" });
  try {
    sheet.mergeCells(startRow, labelCol, startRow, valueCol);
  } catch (e) {
    // Already merged from a previous export pass; keep the header value.
  }

  specs.forEach((spec, index) => {
    const rowNumber = startRow + 1 + index;
    const labelCell = sheet.getCell(rowNumber, labelCol);
    const valueCell = sheet.getCell(rowNumber, valueCol);
    labelCell.value = spec[0];
    valueCell.value = spec[1];
    styleBorderedCell(labelCell, { bold: true });
    styleBorderedCell(valueCell, { align: "right" });
  });

  const labelColumn = sheet.getColumn(labelCol);
  if (!labelColumn.width || labelColumn.width < 22) {
    labelColumn.width = 22;
  }

  return startRow + specs.length;
};

const addQuoteSummaryBlocks = (
  sheet,
  columnKeys,
  bomlines,
  startRow,
  totals,
) => {
  const lastSumRow = getLastQuoteSumExcelRow(bomlines);
  const tariffPercentage = Number(totals.tariffPercentage);
  const safePercent = Number.isFinite(tariffPercentage) ? tariffPercentage : 0;
  const freightCost = Number(totals.freightCostPerVendor);
  const vendorCount = Number(totals.freightVendorCount);
  const freightTotal = Number(totals.freightTotal);
  const resolvedFreight =
    Number.isFinite(freightCost) && Number.isFinite(vendorCount)
      ? Math.round(freightCost * vendorCount * 100) / 100
      : Number.isFinite(freightTotal)
        ? freightTotal
        : 0;
  console.log("vendorCount=>", vendorCount);
  console.log("freightCostPerVendor=>", freightCost);
  const freightLabel =
    Number.isFinite(freightCost) && Number.isFinite(vendorCount)
      ? `Freight ${formatExportNumber(freightCost)}*${formatExportNumber(vendorCount)}`
      : "Freight";

  columnKeys.forEach((key, index) => {
    const match = key.match(/^Quote (\d+)$/);
    if (!match) {
      return;
    }
    const quoteSlot = Number(match[1]);
    const valueCol = index + 1;
    const quoteQtyCol = columnKeys.indexOf(`QuoteQty ${quoteSlot}`) + 1;
    const resultCol = columnKeys.indexOf(`BOM Manager Result ${quoteSlot}`) + 1;
    const labelCol =
      quoteQtyCol > 0
        ? quoteQtyCol
        : resultCol > 0
          ? resultCol
          : valueCol > 1
            ? valueCol - 1
            : valueCol;
    const duplicateCol = valueCol + 1;
    const valueLetter = excelColumnLetter(valueCol);
    const tariffKey = `Tariff Charge ${quoteSlot}`;
    const tariffCol = columnKeys.indexOf(tariffKey) + 1;
    const subtotalCell = `${valueLetter}${startRow}`;
    const tariffAmountCell = `${valueLetter}${startRow + 1}`;
    const freightAmountCell = `${valueLetter}${startRow + 2}`;

    const subtotal = sheet.getCell(startRow, valueCol);
    subtotal.value = {
      formula: `SUM(${valueLetter}2:${valueLetter}${lastSumRow})`,
    };
    styleBorderedCell(subtotal, { bold: true, numFmt: "0.00", align: "right" });

    const tariffLabel = sheet.getCell(startRow + 1, labelCol);
    tariffLabel.value = `Add ${formatExportNumber(safePercent)}%`;
    styleBorderedCell(tariffLabel, { bold: true });
    const tariffValue = sheet.getCell(startRow + 1, valueCol);
    if (tariffCol > 0) {
      const tariffLetter = excelColumnLetter(tariffCol);
      tariffValue.value = {
        formula: `SUM(${tariffLetter}2:${tariffLetter}${lastSumRow})`,
      };
    } else {
      tariffValue.value = {
        formula: `${subtotalCell}*${safePercent}/100`,
      };
    }
    styleBorderedCell(tariffValue, { numFmt: "0.00", align: "right" });

    const freightLabelCell = sheet.getCell(startRow + 2, labelCol);
    freightLabelCell.value = freightLabel;
    styleBorderedCell(freightLabelCell, { bold: true });
    const freightValue = sheet.getCell(startRow + 2, valueCol);
    freightValue.value = resolvedFreight;
    styleBorderedCell(freightValue, { numFmt: "0.00", align: "right" });

    const totalLabel = sheet.getCell(startRow + 3, labelCol);
    totalLabel.value = "Total (without tariff)";
    styleBorderedCell(totalLabel, { bold: true });
    const totalValue = sheet.getCell(startRow + 3, valueCol);
    totalValue.value = {
      formula: `${subtotalCell}+${freightAmountCell}`,
    };
    styleBorderedCell(totalValue, {
      bold: true,
      numFmt: "0.00",
      align: "right",
    });

    const totalTariffLabel = sheet.getCell(startRow + 4, labelCol);
    totalTariffLabel.value = "Total w/ Tariff";
    styleBorderedCell(totalTariffLabel, { bold: true });
    const totalWithTariffCell = `${valueLetter}${startRow + 4}`;
    const totalTariffValue = sheet.getCell(startRow + 4, valueCol);
    totalTariffValue.value = {
      formula: `${subtotalCell}+${tariffAmountCell}+${freightAmountCell}`,
    };
    styleBorderedCell(totalTariffValue, {
      bold: true,
      numFmt: "0.00",
      align: "right",
    });
    const totalTariffDup = sheet.getCell(startRow + 4, duplicateCol);
    totalTariffDup.value = { formula: totalWithTariffCell };
    styleBorderedCell(totalTariffDup, {
      bold: true,
      numFmt: "0.00",
      align: "right",
    });
  });
};

const addExportSummarySection = (sheet, columnKeys, bomlines, totals = {}) => {
  const lastDataExcelRow = (bomlines?.length || 0) + 1;
  const startRow = lastDataExcelRow + 3;
  const linesTableLastRow = addAssemblySpecsTable(
    sheet,
    bomlines,
    columnKeys,
    startRow,
    { mode: "lines", title: "Assembly Specs (Lines)" },
  );
  addAssemblySpecsTable(sheet, bomlines, columnKeys, linesTableLastRow + 3, {
    mode: "qty",
    title: "Assembly Specs (Qty)",
  });

  const hasQuoteColumns = columnKeys.some((key) => /^Quote \d+$/.test(key));
  if (hasQuoteColumns) {
    addQuoteSummaryBlocks(sheet, columnKeys, bomlines, startRow, totals);
    return;
  }

  addLegacyFreightTariffFooterRows(sheet, columnKeys.length, totals);
};

const buildOrderedExportLine = (line, columnKeys) => {
  const ordered = {};
  (columnKeys || []).forEach((key) => {
    ordered[key] =
      line && Object.prototype.hasOwnProperty.call(line, key) ? line[key] : "";
  });
  return ordered;
};

const ExportExcel = async (
  user,
  data,
  invalidQuantityColumns,
  columns,
  exportLocally,
  bomChecklists,
  totals = {},
) => {
  const bomlines = prepareBomlinesForExport(data.bomlines, data.excelData);
  const bomId = data.bomId;
  const columnKeys = getTableColumnKeys(bomlines);
  const sourceLine =
    bomlines.find((line) => isRealBomLine(line) && !isBomSectionRow(line)) ||
    bomlines.find((line) => !isBomSectionRow(line)) ||
    bomlines[0];
  const firstBomLine = buildOrderedExportLine(sourceLine, columnKeys);

  const workbook = new ExcelJS.Workbook();

  var sheet = workbook.addWorksheet("result");
  let rowTemplate = addATemplateRowWithBasicCellFormat(sheet, firstBomLine);
  let inputColumnsCount = addSheetTitleRowAndGetInputColumnsCount(
    rowTemplate,
    firstBomLine,
    columns,
  );

  var lastBomLineIndex = bomlines?.length - 1;

  bomlines.forEach((item, rowIndex) => {
    let rowTemplate = addATemplateRowWithBasicCellFormat(sheet, firstBomLine);
    addSheetDataRow(
      rowTemplate,
      firstBomLine,
      item,
      invalidQuantityColumns,
      rowIndex,
      sheet,
      lastBomLineIndex,
      bomChecklists,
    );
  });

  addExportSummarySection(sheet, columnKeys, bomlines, totals);

  // Try the solution from the following link if required: https://github.com/exceljs/exceljs/issues/83
  // Currently, allocating more width to the result details column.
  for (let i = inputColumnsCount + 4; i <= sheet.columnCount; i++) {
    if ((i - inputColumnsCount - 1) % 3 === 2) {
      sheet.getColumn(i).width = 11;
    } else {
      sheet.getColumn(i).width = 12;
    }
  }
  sheet.getColumn(1).width = 7;
  sheet.getColumn(inputColumnsCount + 1).width = 11;
  sheet.getColumn(inputColumnsCount + 2).width = 12;
  columnKeys.forEach((key, index) => {
    if (/^QuoteQty \d+$/.test(key)) {
      sheet.getColumn(index + 1).width = 24;
    }
  });
  sheet.properties.defaultColWidth = 20;

  let fileNameFromLocalStorage = localStorage.getItem("fileName");
  if (data.issue_report) {
    addIssueReportWorksheet(workbook, normalizeIssueReport(data.issue_report), {
      bomName: fileNameFromLocalStorage || "BOM",
    });
  }
  let fileName =
    "Result-" + (fileNameFromLocalStorage ? fileNameFromLocalStorage : "BOM");
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  await workbook.xlsx.writeBuffer().then(async (data) => {
    const blob = new Blob([data], { type: fileType });
    blob.lastModifiedDate = new Date();
    blob.name = fileName;

    // call backend to store the bom
    await storeResultantBom(user.token, {
      bomId: bomId,
      fileName: fileName,
      file: blob,
    });

    if (exportLocally) {
      FileSaver.saveAs(blob, fileName);
    }
  });
};

export default ExportExcel;
